import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import WagersIntroSlider from "../components/WagersIntroSlider";
import { safeFetch } from "../lib/safeFetch";
import { apiUrl } from "../lib/apiBase";
import { getAuthToken } from "../lib/auth";

const MEMBER_ROUTE = "/membership";
const ADMIN_EMAIL = "peteywhiskers@yahoo.com";

function lower(v) {
  return String(v || "").trim().toLowerCase();
}

function getEscrowParty(wager, side) {
  return wager?.escrow?.[side] || null;
}

function getEscrowState(wager) {
  return lower(wager?.escrow?.state);
}

function isEscrowFunded(wager) {
  return getEscrowState(wager) === "funded";
}

function escrowStatusLabel(wager) {
  const state = getEscrowState(wager);
  if (state === "funded") return "Escrow Funded";
  if (state === "awaiting_opponent") return "Awaiting Opponent Deposit";
  if (state === "awaiting_creator") return "Awaiting Creator Deposit";
  return "Escrow Pending";
}

function refundStatusLabel(wager) {
  const status = lower(wager?.escrow?.refundStatus);
  if (status === "refunded") return "Refunded";
  if (status === "partial") return "Partial Refund";
  if (status === "pending") return "Refund Pending";
  if (status === "manual_review") return "Manual Review";
  return "";
}

function payoutStatusLabel(wager) {
  const status = lower(wager?.escrow?.payoutStatus);
  if (status === "paid") return "Winner Paid";
  if (status === "partial") return "Partial Payout";
  if (status === "pending") return "Payout Pending";
  return "";
}

function depositStatusLabel(party) {
  const status = lower(party?.status);
  if (status === "funded") return "Deposit complete";
  if (status === "processing") return "Checkout created";
  return "Deposit pending";
}

function msToClock(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

function money(amountCents) {
  const n = Number(amountCents || 0);
  return `$${(n / 100).toFixed(2)}`;
}

function winnerDisplay(wager) {
  return (
    wager?.winner?.username ||
    wager?.winner?.tag ||
    wager?.winner?.email ||
    "Pending"
  );
}

function loserDisplay(wager) {
  return (
    wager?.loser?.username ||
    wager?.loser?.tag ||
    wager?.loser?.email ||
    "Pending"
  );
}

export default function WagerRoom() {
  const { id } = useParams();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const action = params.get("action") || "";
  const paymentState = params.get("payment") || "";
  const paymentSide = params.get("side") || "";
  const connectState = params.get("connect") || "";

  const [email, setEmail] = useState("");
  const [wager, setWager] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const [submittingResult, setSubmittingResult] = useState(false);
  const [paymentBusy, setPaymentBusy] = useState("");
  const [paymentNotice, setPaymentNotice] = useState("");
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectBusy, setConnectBusy] = useState("");
  const [connectNotice, setConnectNotice] = useState("");
  const [adminBusy, setAdminBusy] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [adminNotice, setAdminNotice] = useState("");

  const listRef = useRef(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("member_email");
      if (saved) setEmail(saved);
    } catch {}
  }, []);

  useEffect(() => {
    if (!wager) return;
    if (String(wager.status || "").toLowerCase() !== "active") return;

    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [wager]);

  const load = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      const token = getAuthToken();
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      const w = await safeFetch(
        apiUrl(
          `/wagers/${encodeURIComponent(id)}?email=${encodeURIComponent(email || "")}`
        ),
        { headers: authHeaders }
      );
      setWager(w?.wager || null);

      if (email) {
        const m = await safeFetch(
          apiUrl(
            `/wagers/${encodeURIComponent(id)}/messages?email=${encodeURIComponent(email)}`
          ),
          { headers: authHeaders }
        );
        setMessages(
          Array.isArray(m?.messages)
            ? m.messages
            : Array.isArray(m?.items)
            ? m.items
            : []
        );
      } else {
        setMessages([]);
      }
    } catch {
      setWager(null);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [id, email]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (paymentState === "success") {
      setPaymentNotice(
        `${
          paymentSide === "creator"
            ? "Creator"
            : paymentSide === "opponent"
            ? "Opponent"
            : "Deposit"
        } payment returned from Stripe. Refreshing escrow status...`
      );
      void load();
    } else if (paymentState === "cancel") {
      setPaymentNotice(
        `${
          paymentSide === "creator"
            ? "Creator"
            : paymentSide === "opponent"
            ? "Opponent"
            : "Deposit"
        } payment was canceled before completion.`
      );
    } else {
      setPaymentNotice("");
    }
  }, [paymentState, paymentSide, load]);

  useEffect(() => {
    if (connectState === "return") {
      setConnectNotice(
        "Returned from Stripe Connect. Refreshing payout account status..."
      );
    } else if (connectState === "refresh") {
      setConnectNotice(
        "Stripe onboarding session refreshed. Continue completing your payout details."
      );
    } else {
      setConnectNotice("");
    }
  }, [connectState]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const status = lower(wager?.status);
  const creatorEmail = lower(wager?.creatorEmail || wager?.creator?.email);
  const opponentEmail = lower(wager?.opponentEmail || wager?.opponent?.email);
  const meEmail = lower(email);

  const isCreator = !!meEmail && meEmail === creatorEmail;
  const isOpponent = !!meEmail && meEmail === opponentEmail;
  const isAdmin = meEmail === lower(ADMIN_EMAIL);
  const youAreParticipant = isCreator || isOpponent;
  const mySide = isCreator ? "creator" : isOpponent ? "opponent" : "";
  const escrowFunded = isEscrowFunded(wager);
  const creatorEscrow = getEscrowParty(wager, "creator");
  const opponentEscrow = getEscrowParty(wager, "opponent");
  const myEscrow = mySide ? getEscrowParty(wager, mySide) : null;
  const myDepositFunded = lower(myEscrow?.status) === "funded";
  const canPayDeposit =
    !!wager &&
    !!mySide &&
    (status === "invited" || status === "active") &&
    !myDepositFunded;

  const canAccept = status === "invited" && isOpponent;

  const expiresAt = Number(wager?.timers?.expiresAt || wager?.expiresAt || 0);
  const timeLeft = expiresAt ? Math.max(0, expiresAt - nowTs) : 0;
  const isExpired = !!expiresAt && timeLeft <= 0;

  const game = wager?.match?.game || wager?.game || "—";
  const mode = wager?.match?.mode || wager?.mode || "—";
  const platform = wager?.match?.platform || wager?.platform || "—";
  const rules = wager?.match?.rules || wager?.rules || "";
  const amountCents =
    typeof wager?.amountCents === "number"
      ? wager.amountCents
      : Number(wager?.stake?.amountCents || 0);

  const creatorTag =
    wager?.creatorTag ||
    wager?.creator?.gamerTag ||
    wager?.creator?.username ||
    "(no tag)";
  const opponentTag =
    wager?.opponentTag ||
    wager?.opponent?.gamerTag ||
    wager?.opponent?.username ||
    "(no tag)";

  const creatorReport = String(wager?.result?.creatorReport || "").toLowerCase();
  const opponentReport = String(wager?.result?.opponentReport || "").toLowerCase();
  const myReport = isCreator ? creatorReport : isOpponent ? opponentReport : "";
  const otherReport = isCreator ? opponentReport : isOpponent ? creatorReport : "";
  const alreadyReported = !!myReport;

  const canReport =
    status === "active" &&
    escrowFunded &&
    youAreParticipant &&
    !alreadyReported &&
    (!isExpired || !!otherReport || !otherReport);

  const accept = async () => {
    const enteredTag = prompt("Enter your gamer tag for this match:");
    if (!enteredTag) return;

    try {
      const token = getAuthToken();
      await safeFetch(apiUrl(`/wagers/${encodeURIComponent(id)}/accept`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          opponentEmail: email,
          opponentTag: enteredTag,
        }),
      });
      await load();
    } catch (e) {
      alert(e?.message || "Unable to accept invite.");
    }
  };

  const startCheckout = async () => {
    if (!mySide || !id) return;

    try {
      setPaymentBusy(mySide);
      setPaymentNotice("");
      const token = getAuthToken();
      const js = await safeFetch(
        apiUrl(`/wagers/${encodeURIComponent(id)}/checkout-session`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ side: mySide }),
        }
      );

      if (js?.alreadyFunded) {
        setPaymentNotice("Your deposit is already marked as funded.");
        await load();
        return;
      }

      if (js?.url) {
        window.location.href = js.url;
        return;
      }

      throw new Error(js?.message || "Stripe checkout URL was missing.");
    } catch (e) {
      alert(e?.message || "Unable to start escrow checkout.");
      await load();
    } finally {
      setPaymentBusy("");
    }
  };

  const report = async (result) => {
    if (submittingResult || alreadyReported) return;

    try {
      setSubmittingResult(true);
      const token = getAuthToken();
      await safeFetch(apiUrl(`/wagers/${encodeURIComponent(id)}/confirm`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          reporterEmail: email,
          result,
        }),
      });
      await load();
    } catch (e) {
      alert(e?.message || "Unable to report result.");
    } finally {
      setSubmittingResult(false);
    }
  };

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;

    try {
      const token = getAuthToken();
      await safeFetch(apiUrl(`/wagers/${encodeURIComponent(id)}/messages`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          email,
          text: trimmed,
        }),
      });
      setText("");
      await load();
    } catch (e) {
      alert(e?.message || "Unable to send message.");
    }
  };

  const adminResolve = async (actionType, winnerSide = "") => {
    if (!id || !isAdmin) return;

    try {
      setAdminBusy(actionType);
      setAdminNotice("");

      const token = getAuthToken();
      if (!token) throw new Error("Sign in first.");

      const body = {
        action: actionType,
        note: adminNote.trim(),
      };

      if (actionType === "force_win") {
        body.winnerSide = winnerSide;
      }

      await safeFetch(apiUrl(`/wagers/${encodeURIComponent(id)}/admin-resolve`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (actionType === "force_win") {
        setAdminNotice(
          `Admin override applied. ${winnerSide} forced as winner.`
        );
      } else if (actionType === "void") {
        setAdminNotice("Admin void applied. Refund flow triggered.");
      } else if (actionType === "dispute") {
        setAdminNotice("Admin dispute refund applied. Refund flow triggered.");
      }

      await load();
    } catch (e) {
      setAdminNotice(e?.message || "Unable to apply admin action.");
    } finally {
      setAdminBusy("");
    }
  };

  useEffect(() => {
    const token = getAuthToken();
    if (!token || !youAreParticipant) {
      setConnectStatus(null);
      return;
    }

    let cancelled = false;

    const loadConnectStatus = async () => {
      try {
        const js = await safeFetch(apiUrl(`/connect/status`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setConnectStatus(js || null);
        }
      } catch (e) {
        if (!cancelled) {
          setConnectStatus(null);
          setConnectNotice(e?.message || "Unable to load payout account status.");
        }
      }
    };

    void loadConnectStatus();

    return () => {
      cancelled = true;
    };
  }, [youAreParticipant]);

  const roomTitle = `${game} • ${mode}`;

  const hasAuthToken = !!getAuthToken();
  const connectReady = !!connectStatus?.payoutsEnabled;
  const connectStarted = !!connectStatus?.hasAccount;

  const startConnectOnboarding = async () => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error("Sign in first to set up your payout account.");
      setConnectBusy("onboard");
      setConnectNotice("");
      const js = await safeFetch(apiUrl("/connect/onboard"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ returnPath: `/wagers/${encodeURIComponent(id)}` }),
      });
      if (js?.url) {
        window.location.href = js.url;
        return;
      }
      throw new Error(js?.message || "Stripe onboarding link was missing.");
    } catch (e) {
      setConnectNotice(e?.message || "Unable to start Stripe Connect onboarding.");
    } finally {
      setConnectBusy("");
    }
  };

  const openConnectDashboard = async () => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error("Sign in first to open your payout dashboard.");
      setConnectBusy("dashboard");
      setConnectNotice("");
      const js = await safeFetch(apiUrl("/connect/dashboard-link"), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (js?.url) {
        window.location.href = js.url;
        return;
      }
      throw new Error(js?.message || "Stripe dashboard link was missing.");
    } catch (e) {
      setConnectNotice(e?.message || "Unable to open payout dashboard.");
    } finally {
      setConnectBusy("");
    }
  };

  let escrowBanner = "";
  if ((status === "invited" || status === "active") && !escrowFunded) {
    escrowBanner = `${escrowStatusLabel(
      wager
    )}. The match should not begin until both deposits are funded.`;
  } else if (escrowFunded && (status === "invited" || status === "active")) {
    escrowBanner = "Both deposits are funded. This wager is ready to play.";
  }

  let resultBanner = "";
  if (status === "completed") {
    resultBanner = `Final Result: ${winnerDisplay(wager)} defeated ${loserDisplay(
      wager
    )}.`;
  } else if (status === "disputed") {
    resultBanner = "This room is disputed. Submit proof to support for review.";
  } else if (status === "voided") {
    const refundLabel = refundStatusLabel(wager);
    resultBanner = refundLabel
      ? `This room expired before completion and has been voided. ${refundLabel}.`
      : "This room expired before completion and has been voided.";
  } else if (alreadyReported && !otherReport && !isExpired) {
    resultBanner = `You reported ${myReport.toUpperCase()}. Waiting on the other player.`;
  } else if (alreadyReported && !otherReport && isExpired) {
    resultBanner = `You reported ${myReport.toUpperCase()}. Final resolution is being applied.`;
  }

  return (
    <div className="page wager-room-page">
      <div style={{ marginBottom: 18 }}>
        <WagersIntroSlider />
      </div>

      <section style={styles.shell}>
        <div style={styles.headerRow}>
          <div>
            <div style={styles.kicker}>Wager Room</div>
            <h1 style={styles.title}>{roomTitle}</h1>
            <p style={styles.subtitle}>
              Manage the private member-vs-member match room, confirm the result,
              and keep all lobby coordination in one place.
            </p>
          </div>

          <div style={styles.emailCard}>
            <label style={styles.label}>Your Email</label>
            <input
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              style={styles.input}
            />
            <div style={styles.emailHelp}>
              Not a member?{" "}
              <Link to={MEMBER_ROUTE} style={styles.inlineLink}>
                Become a Member
              </Link>
            </div>
          </div>
        </div>

        {loading && <div style={styles.notice}>Loading wager room…</div>}
        {!loading && !wager && (
          <div style={styles.notice}>Wager room not found.</div>
        )}

        {!!wager && (
          <>
            <div style={styles.topGrid}>
              <div style={styles.heroCard}>
                <div style={styles.heroTop}>
                  <div>
                    <div style={styles.metaKicker}>Match Summary</div>
                    <div style={styles.heroTitle}>{game}</div>
                    <div style={styles.heroMode}>{mode}</div>
                  </div>
                  <div style={styles.amountPill}>{money(amountCents)}</div>
                </div>

                <div style={styles.statGrid}>
                  <div style={styles.statBox}>
                    <div style={styles.statLabel}>Platform</div>
                    <div style={styles.statValue}>{platform}</div>
                  </div>
                  <div style={styles.statBox}>
                    <div style={styles.statLabel}>Status</div>
                    <div style={styles.statValue}>
                      {status ? status.toUpperCase() : "—"}
                    </div>
                  </div>
                  <div style={styles.statBox}>
                    <div style={styles.statLabel}>Escrow</div>
                    <div style={styles.statValueSmall}>
                      {escrowStatusLabel(wager)}
                    </div>
                  </div>
                  <div style={styles.statBox}>
                    <div style={styles.statLabel}>Timer</div>
                    <div style={styles.statValue}>
                      {status === "active" && expiresAt ? msToClock(timeLeft) : "—"}
                    </div>
                  </div>
                </div>

                {!!rules && (
                  <div style={styles.rulesBox}>
                    <div style={styles.statLabel}>Rules</div>
                    <div style={styles.rulesText}>{rules}</div>
                  </div>
                )}
              </div>

              <div style={styles.sideStack}>
                <div style={styles.sideCard}>
                  <div style={styles.sideHeading}>Players</div>

                  <div style={styles.playerCard}>
                    <div style={styles.playerLabel}>Creator</div>
                    <div style={styles.playerName}>{creatorTag}</div>
                    <div style={styles.playerSub}>{creatorEmail}</div>
                  </div>

                  <div style={styles.playerCard}>
                    <div style={styles.playerLabel}>Opponent</div>
                    <div style={styles.playerName}>{opponentTag}</div>
                    <div style={styles.playerSub}>
                      {opponentEmail || "Waiting on opponent"}
                    </div>
                  </div>
                </div>

                <div style={styles.sideCard}>
                  <div style={styles.sideHeading}>Escrow</div>

                  <div style={styles.playerCard}>
                    <div style={styles.playerLabel}>Overall</div>
                    <div style={styles.playerName}>{escrowStatusLabel(wager)}</div>
                    <div style={styles.playerSub}>
                      Total pot:{" "}
                      {money(
                        Number(wager?.escrow?.totalAmountCents || amountCents * 2 || 0)
                      )}
                    </div>
                  </div>

                  <div style={styles.playerCard}>
                    <div style={styles.playerLabel}>Creator deposit</div>
                    <div style={styles.playerName}>
                      {depositStatusLabel(creatorEscrow)}
                    </div>
                    <div style={styles.playerSub}>
                      {creatorEmail || "Creator"} •{" "}
                      {money(Number(creatorEscrow?.amountCents || amountCents || 0))}
                    </div>
                  </div>

                  <div style={styles.playerCard}>
                    <div style={styles.playerLabel}>Opponent deposit</div>
                    <div style={styles.playerName}>
                      {depositStatusLabel(opponentEscrow)}
                    </div>
                    <div style={styles.playerSub}>
                      {opponentEmail || "Opponent"} •{" "}
                      {money(Number(opponentEscrow?.amountCents || amountCents || 0))}
                    </div>
                  </div>

                  {!!refundStatusLabel(wager) && (
                    <div style={styles.playerCard}>
                      <div style={styles.playerLabel}>Refund</div>
                      <div style={styles.playerName}>{refundStatusLabel(wager)}</div>
                      <div style={styles.playerSub}>
                        {wager?.escrow?.refundedAt
                          ? `Updated ${new Date(Number(wager.escrow.refundedAt)).toLocaleString()}`
                          : "Refund processing state tracked here."}
                      </div>
                    </div>
                  )}

                  {!!payoutStatusLabel(wager) && (
                    <div style={styles.playerCard}>
                      <div style={styles.playerLabel}>Winner Payout</div>
                      <div style={styles.playerName}>{payoutStatusLabel(wager)}</div>
                      <div style={styles.playerSub}>
                        {wager?.escrow?.transferId
                          ? `Transfer ID: ${wager.escrow.transferId}`
                          : "Winner release tracking."}
                      </div>
                    </div>
                  )}

                  {!!wager?.escrow?.lastRefundError && (
                    <div style={styles.notice}>
                      Refund issue: {wager.escrow.lastRefundError}
                    </div>
                  )}

                  {!!wager?.escrow?.lastPayoutError && (
                    <div style={styles.notice}>
                      Payout issue: {wager.escrow.lastPayoutError}
                    </div>
                  )}
                </div>

                <div style={styles.sideCard}>
                  <div style={styles.sideHeading}>Payout Account</div>

                  {!youAreParticipant ? (
                    <div style={styles.notice}>
                      Only wager participants can set up payout accounts here.
                    </div>
                  ) : !hasAuthToken ? (
                    <div style={styles.notice}>
                      Sign in to connect your Stripe payout account before winner
                      payouts go live.
                    </div>
                  ) : connectReady ? (
                    <>
                      <div style={styles.noticeStrong}>
                        Your payout account is ready for automated winner releases.
                      </div>
                      <button
                        className="btn"
                        onClick={openConnectDashboard}
                        disabled={connectBusy === "dashboard"}
                        style={{
                          ...styles.btn,
                          ...styles.secondaryBtn,
                          ...(connectBusy === "dashboard" ? styles.disabledBtn : {}),
                        }}
                      >
                        {connectBusy === "dashboard"
                          ? "Opening Dashboard..."
                          : "Open Stripe Payout Dashboard"}
                      </button>
                    </>
                  ) : (
                    <>
                      <div style={styles.notice}>
                        {connectStarted
                          ? "Finish Stripe onboarding now so your winnings can be transferred automatically once payouts are released."
                          : "Create your Stripe payout account now so Phase 3 winner release can transfer funds to the correct person."}
                      </div>
                      <button
                        className="btn primary"
                        onClick={startConnectOnboarding}
                        disabled={connectBusy === "onboard"}
                        style={{
                          ...styles.btn,
                          ...styles.primaryBtn,
                          ...(connectBusy === "onboard" ? styles.disabledBtn : {}),
                        }}
                      >
                        {connectBusy === "onboard"
                          ? "Opening Stripe Connect..."
                          : connectStarted
                          ? "Continue Stripe Onboarding"
                          : "Set Up Stripe Payout Account"}
                      </button>
                    </>
                  )}

                  {connectStatus?.requirements?.currentlyDue?.length > 0 && (
                    <div style={styles.connectMeta}>
                      Still needed:{" "}
                      {connectStatus.requirements.currentlyDue.join(", ")}
                    </div>
                  )}

                  {connectNotice && <div style={styles.notice}>{connectNotice}</div>}
                </div>

                <div style={styles.sideCard}>
                  <div style={styles.sideHeading}>Room Actions</div>

                  {canAccept && (
                    <button
                      className="btn primary"
                      onClick={accept}
                      style={{ ...styles.btn, ...styles.primaryBtn }}
                    >
                      Accept Invite
                    </button>
                  )}

                  {canReport && (
                    <div style={styles.actionGrid}>
                      <button
                        className="btn primary"
                        onClick={() => report("win")}
                        disabled={submittingResult}
                        style={{
                          ...styles.btn,
                          ...styles.primaryBtn,
                          ...(submittingResult ? styles.disabledBtn : {}),
                        }}
                      >
                        {submittingResult ? "Submitting..." : "Report Win"}
                      </button>
                      <button
                        className="btn"
                        onClick={() => report("loss")}
                        disabled={submittingResult}
                        style={{
                          ...styles.btn,
                          ...styles.secondaryBtn,
                          ...(submittingResult ? styles.disabledBtn : {}),
                        }}
                      >
                        {submittingResult ? "Submitting..." : "Report Loss"}
                      </button>
                    </div>
                  )}

                  {alreadyReported && (
                    <div style={styles.noticeStrong}>
                      You already reported <strong>{myReport.toUpperCase()}</strong>.
                    </div>
                  )}

                  {!canAccept && !canReport && status === "invited" && (
                    <div style={styles.notice}>
                      Waiting on the invited opponent to accept this room.
                    </div>
                  )}

                  {canPayDeposit && (
                    <button
                      className="btn primary"
                      onClick={startCheckout}
                      disabled={paymentBusy === mySide}
                      style={{
                        ...styles.btn,
                        ...styles.primaryBtn,
                        ...(paymentBusy === mySide ? styles.disabledBtn : {}),
                      }}
                    >
                      {paymentBusy === mySide
                        ? "Opening Checkout..."
                        : `Pay ${
                            mySide === "creator" ? "Creator" : "Opponent"
                          } Deposit`}
                    </button>
                  )}

                  {paymentNotice && <div style={styles.notice}>{paymentNotice}</div>}
                  {escrowBanner && <div style={styles.notice}>{escrowBanner}</div>}
                  {resultBanner && <div style={styles.notice}>{resultBanner}</div>}

                  <Link
                    className="btn"
                    to="/wagers"
                    style={{ ...styles.btn, ...styles.secondaryBtn, marginTop: 10 }}
                  >
                    Back to Wagers
                  </Link>
                </div>

                {isAdmin && (
                  <div style={styles.sideCard}>
                    <div style={styles.sideHeading}>Admin Resolve</div>

                    <div style={styles.notice}>
                      Use these only for moderation decisions. Force win pays the selected side.
                      Void and dispute both trigger refunds.
                    </div>

                    <label style={styles.label}>Admin note</label>
                    <textarea
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      placeholder="Optional moderator note..."
                      style={styles.textarea}
                    />

                    <div style={styles.actionGrid}>
                      <button
                        className="btn primary"
                        onClick={() => adminResolve("force_win", "creator")}
                        disabled={adminBusy === "force_win"}
                        style={{
                          ...styles.btn,
                          ...styles.primaryBtn,
                          ...(adminBusy === "force_win" ? styles.disabledBtn : {}),
                        }}
                      >
                        {adminBusy === "force_win"
                          ? "Applying..."
                          : "Force Creator Win"}
                      </button>

                      <button
                        className="btn primary"
                        onClick={() => adminResolve("force_win", "opponent")}
                        disabled={adminBusy === "force_win"}
                        style={{
                          ...styles.btn,
                          ...styles.primaryBtn,
                          ...(adminBusy === "force_win" ? styles.disabledBtn : {}),
                        }}
                      >
                        {adminBusy === "force_win"
                          ? "Applying..."
                          : "Force Opponent Win"}
                      </button>

                      <button
                        className="btn"
                        onClick={() => adminResolve("void")}
                        disabled={adminBusy === "void"}
                        style={{
                          ...styles.btn,
                          ...styles.secondaryBtn,
                          ...(adminBusy === "void" ? styles.disabledBtn : {}),
                        }}
                      >
                        {adminBusy === "void" ? "Voiding..." : "Void + Refund"}
                      </button>

                      <button
                        className="btn"
                        onClick={() => adminResolve("dispute")}
                        disabled={adminBusy === "dispute"}
                        style={{
                          ...styles.btn,
                          ...styles.secondaryBtn,
                          ...(adminBusy === "dispute" ? styles.disabledBtn : {}),
                        }}
                      >
                        {adminBusy === "dispute" ? "Resolving..." : "Dispute + Refund"}
                      </button>
                    </div>

                    {adminNotice && <div style={styles.noticeStrong}>{adminNotice}</div>}
                  </div>
                )}
              </div>
            </div>

            {(action === "report" ||
              canReport ||
              alreadyReported ||
              status === "completed") && (
              <div style={styles.reportCard}>
                <div style={styles.sideHeading}>Confirm the Outcome</div>
                <p style={styles.reportText}>
                  Report whether you won or lost this match. If both players report
                  opposite matching outcomes, the room completes. If the other player
                  never reports before expiry, the submitted result can resolve the room
                  at expiration.
                </p>

                {status === "completed" ? (
                  <div style={styles.noticeStrong}>
                    Winner: <strong>{winnerDisplay(wager)}</strong>
                    {wager?.loser ? (
                      <>
                        {" "}
                        — defeated <strong>{loserDisplay(wager)}</strong>
                      </>
                    ) : null}
                  </div>
                ) : alreadyReported ? (
                  <div style={styles.noticeStrong}>
                    You already submitted <strong>{myReport.toUpperCase()}</strong>.
                    Waiting on opponent or expiry resolution.
                  </div>
                ) : canReport ? (
                  <div style={styles.reportGrid}>
                    <button
                      className="btn primary"
                      onClick={() => report("win")}
                      disabled={submittingResult}
                      style={{
                        ...styles.btn,
                        ...styles.primaryBtn,
                        ...(submittingResult ? styles.disabledBtn : {}),
                      }}
                    >
                      {submittingResult ? "Submitting..." : "I Won"}
                    </button>
                    <button
                      className="btn"
                      onClick={() => report("loss")}
                      disabled={submittingResult}
                      style={{
                        ...styles.btn,
                        ...styles.secondaryBtn,
                        ...(submittingResult ? styles.disabledBtn : {}),
                      }}
                    >
                      {submittingResult ? "Submitting..." : "I Lost"}
                    </button>
                  </div>
                ) : (
                  <div style={styles.notice}>
                    Reporting unlocks after both deposits are funded and the room is
                    active.
                  </div>
                )}
              </div>
            )}

            <div style={styles.threadCard}>
              <div style={styles.sideHeading}>Wager Thread</div>
              <div style={styles.threadSub}>
                Share lobby codes, screenshots, readiness, and any match notes here.
              </div>

              {!email ? (
                <div style={styles.notice}>
                  Enter your email above to load and send messages.
                </div>
              ) : !youAreParticipant ? (
                <div style={styles.notice}>
                  Only participants can view and send wager messages.
                </div>
              ) : (
                <>
                  <div ref={listRef} style={styles.messageList}>
                    {messages.length === 0 ? (
                      <div style={styles.emptyState}>
                        No messages yet. Share lobby codes / rules here.
                      </div>
                    ) : (
                      messages.map((m) => (
                        <div key={m.id} style={styles.messageItem}>
                          <div style={styles.messageMeta}>
                            <span>{m.email}</span>
                            <span>{new Date(m.ts).toLocaleString()}</span>
                          </div>
                          <div style={styles.messageText}>{m.text}</div>
                        </div>
                      ))
                    )}
                  </div>

                  <div style={styles.composeRow}>
                    <input
                      className="input"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Type a message… (lobby code, rules, ready check)"
                      style={{ ...styles.input, flex: 1 }}
                    />
                    <button
                      className="btn primary"
                      onClick={send}
                      style={{ ...styles.btn, ...styles.primaryBtn, width: 140 }}
                    >
                      Send
                    </button>
                  </div>

                  <div style={styles.disputeText}>
                    Disputes: email <strong>support@gastreaming.live</strong> with proof
                    (screenshots / footage) + wager ID.
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

const styles = {
  shell: {
    borderRadius: 22,
    padding: 20,
    background: "rgba(15, 10, 14, 0.78)",
    border: "1px solid rgba(255, 30, 30, 0.18)",
    boxShadow: "0 22px 80px rgba(0,0,0,0.55)",
    backdropFilter: "blur(10px)",
    marginBottom: 18,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 18,
    flexWrap: "wrap",
  },
  kicker: {
    color: "rgba(255,255,255,0.74)",
    textTransform: "uppercase",
    letterSpacing: 1.4,
    fontSize: 12,
    marginBottom: 10,
  },
  title: {
    margin: 0,
    color: "#fff",
    fontSize: 34,
    lineHeight: 1.05,
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  subtitle: {
    margin: "10px 0 0",
    color: "rgba(235,240,255,0.82)",
    lineHeight: 1.55,
    maxWidth: 860,
  },
  emailCard: {
    minWidth: 300,
    maxWidth: 360,
    width: "100%",
    borderRadius: 18,
    padding: 16,
    background: "rgba(0,0,0,0.28)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  label: {
    display: "block",
    marginBottom: 8,
    color: "rgba(255,255,255,0.94)",
    fontWeight: 700,
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: 0.45,
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(9,9,12,0.72)",
    color: "#fff",
    padding: "14px 16px",
    outline: "none",
    fontSize: 16,
  },
  textarea: {
    width: "100%",
    minHeight: 100,
    boxSizing: "border-box",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(9,9,12,0.72)",
    color: "#fff",
    padding: "14px 16px",
    outline: "none",
    fontSize: 15,
    resize: "vertical",
    marginBottom: 12,
  },
  emailHelp: {
    marginTop: 8,
    fontSize: 13,
    color: "rgba(235,240,255,0.72)",
  },
  inlineLink: {
    color: "#fff",
    textDecoration: "underline",
    fontWeight: 700,
  },
  notice: {
    marginTop: 12,
    borderRadius: 14,
    padding: "12px 14px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(235,240,255,0.94)",
    lineHeight: 1.5,
  },
  noticeStrong: {
    marginTop: 12,
    borderRadius: 14,
    padding: "12px 14px",
    background: "rgba(255,0,0,0.08)",
    border: "1px solid rgba(255,50,50,0.18)",
    color: "#fff",
    lineHeight: 1.5,
    fontWeight: 600,
  },
  topGrid: {
    marginTop: 18,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.4fr) minmax(320px, 0.9fr)",
    gap: 16,
  },
  heroCard: {
    borderRadius: 18,
    padding: 18,
    background: "rgba(0,0,0,0.34)",
    border: "1px solid rgba(255,255,255,0.10)",
  },
  heroTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    flexWrap: "wrap",
  },
  metaKicker: {
    color: "rgba(255,255,255,0.70)",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  heroTitle: {
    color: "#fff",
    fontSize: 30,
    fontWeight: 800,
    fontFamily: 'Georgia, "Times New Roman", serif',
    lineHeight: 1.1,
  },
  heroMode: {
    marginTop: 6,
    color: "rgba(235,240,255,0.88)",
    fontSize: 22,
    fontWeight: 700,
  },
  amountPill: {
    borderRadius: 999,
    padding: "12px 18px",
    background: "linear-gradient(180deg, #ff2020 0%, #e10000 100%)",
    border: "1px solid rgba(255,30,30,0.28)",
    boxShadow: "0 14px 30px rgba(200,0,0,0.22)",
    color: "#fff",
    fontWeight: 800,
    fontSize: 22,
    whiteSpace: "nowrap",
  },
  statGrid: {
    marginTop: 16,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  statBox: {
    borderRadius: 14,
    padding: 14,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  statLabel: {
    color: "rgba(255,255,255,0.70)",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  statValue: {
    color: "#fff",
    fontSize: 21,
    fontWeight: 800,
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  statValueSmall: {
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    wordBreak: "break-all",
  },
  rulesBox: {
    marginTop: 16,
    borderRadius: 14,
    padding: 14,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  rulesText: {
    color: "rgba(235,240,255,0.92)",
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
  },
  sideStack: {
    display: "grid",
    gap: 16,
    alignSelf: "start",
  },
  sideCard: {
    borderRadius: 18,
    padding: 18,
    background: "rgba(0,0,0,0.34)",
    border: "1px solid rgba(255,255,255,0.10)",
  },
  sideHeading: {
    color: "#fff",
    fontSize: 24,
    fontWeight: 800,
    fontFamily: 'Georgia, "Times New Roman", serif',
    marginBottom: 12,
  },
  playerCard: {
    borderRadius: 14,
    padding: 14,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    marginBottom: 10,
  },
  playerLabel: {
    color: "rgba(255,255,255,0.70)",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 6,
  },
  playerName: {
    color: "#fff",
    fontSize: 22,
    fontWeight: 800,
    fontFamily: 'Georgia, "Times New Roman", serif',
    marginBottom: 4,
  },
  playerSub: {
    color: "rgba(235,240,255,0.78)",
    lineHeight: 1.4,
    wordBreak: "break-word",
  },
  btn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 52,
    padding: "14px 18px",
    borderRadius: 14,
    textDecoration: "none",
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(0,0,0,0.28)",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
    width: "100%",
    boxSizing: "border-box",
  },
  primaryBtn: {
    background: "linear-gradient(180deg, #ff2020 0%, #e10000 100%)",
    border: "1px solid rgba(255,30,30,0.28)",
    boxShadow: "0 14px 30px rgba(200,0,0,0.22)",
  },
  secondaryBtn: {
    background: "rgba(0,0,0,0.28)",
  },
  disabledBtn: {
    opacity: 0.65,
    cursor: "not-allowed",
  },
  actionGrid: {
    display: "grid",
    gap: 10,
  },
  reportCard: {
    marginTop: 16,
    borderRadius: 18,
    padding: 18,
    background: "rgba(0,0,0,0.34)",
    border: "1px solid rgba(255,255,255,0.10)",
  },
  reportText: {
    color: "rgba(235,240,255,0.84)",
    lineHeight: 1.55,
    margin: "0 0 14px",
  },
  reportGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  threadCard: {
    marginTop: 16,
    borderRadius: 18,
    padding: 18,
    background: "rgba(0,0,0,0.34)",
    border: "1px solid rgba(255,255,255,0.10)",
  },
  threadSub: {
    color: "rgba(235,240,255,0.74)",
    lineHeight: 1.45,
    marginBottom: 12,
  },
  messageList: {
    maxHeight: 340,
    overflowY: "auto",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 14,
    padding: 12,
    background: "rgba(255,255,255,0.03)",
  },
  emptyState: {
    color: "rgba(235,240,255,0.72)",
    minHeight: 80,
    display: "flex",
    alignItems: "center",
  },
  messageItem: {
    padding: "10px 8px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  messageMeta: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    fontSize: 12,
    color: "rgba(255,255,255,0.62)",
    marginBottom: 6,
    flexWrap: "wrap",
  },
  messageText: {
    whiteSpace: "pre-wrap",
    color: "#fff",
    lineHeight: 1.5,
  },
  composeRow: {
    display: "flex",
    gap: 10,
    marginTop: 12,
    flexWrap: "wrap",
  },
  disputeText: {
    marginTop: 10,
    color: "rgba(235,240,255,0.70)",
    fontSize: 12,
    lineHeight: 1.5,
  },
  connectMeta: {
    marginTop: 10,
    color: "rgba(235,240,255,0.72)",
    lineHeight: 1.5,
    fontSize: 12,
    wordBreak: "break-word",
  },
};